import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { motion } from 'framer-motion'
import { ArrowRight, Check, Eye, EyeOff, Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'

const signInSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  remember_me: z.boolean().optional(),
})

const signUpSchema = z.object({
  first_name: z.string().min(2, 'First name must be at least 2 characters'),
  last_name: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
  marketing_consent: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type SignInForm = z.infer<typeof signInSchema>
type SignUpForm = z.infer<typeof signUpSchema>

const inputClass = "w-full px-6 py-4 rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/50 focus:outline-none hover:border-white/30 focus:border-white/40 transition-all"

export default function Auth() {
  const [isSignUp, setIsSignUp] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { user, signIn, signUp, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) {
      navigate('/portal', { replace: true })
    }
  }, [user, navigate])

  const signInForm = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
      remember_me: true,
    },
  })

  const signUpForm = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirmPassword: '',
      marketing_consent: false,
    },
  })

  const onSignIn = async (data: SignInForm) => {
    const result = await signIn({ ...data, remember_me: data.remember_me ?? true })
    if (result.error) {
      toast.error(result.error)
    } else {
      toast.success('Signed in successfully!')
      navigate('/portal')
    }
  }

  const onSignUp = async (data: SignUpForm) => {
    const result = await signUp({
      email: data.email,
      password: data.password,
      first_name: data.first_name,
      last_name: data.last_name,
      full_name: `${data.first_name} ${data.last_name}`.trim(),
      marketing_consent: data.marketing_consent ?? false,
    })
    if (result.error) {
      toast.error(result.error)
    } else {
      toast.success('Account created successfully! Please check your email to verify your account.')
      setIsSignUp(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-accent font-sans text-white selection:bg-white selection:text-accent">
      <Navbar />
      <main className="relative flex-1 pt-32 pb-24">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-10 mix-blend-overlay pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-accent/80 via-transparent to-accent pointer-events-none" />
        <div className="container relative z-10 max-w-xl mx-auto px-8">
          <div className="space-y-8 animate-fade-up">
            <div>
              <h1 className="text-3xl font-bold font-heading text-white">
                {isSignUp ? 'Create account' : 'Welcome back'}
              </h1>
              <p className="text-white/60 text-sm mt-2">
                {isSignUp
                  ? 'Enter your details to create your account'
                  : 'Sign in to your account to continue'}
              </p>
            </div>

            <div className="relative flex gap-2 p-1 rounded-lg bg-white/5 border border-white/10">
              <motion.div
                className="absolute top-1 bottom-1 rounded-md bg-white"
                layoutId="auth-login-toggle-bg"
                style={{ width: 'calc(50% - 4px)' }}
                animate={{ left: !isSignUp ? 4 : 'calc(50% + 0px)' }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
              <button
                type="button"
                onClick={() => setIsSignUp(false)}
                className={`relative z-10 flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                  !isSignUp ? 'text-accent' : 'text-white/70 hover:text-white'
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => setIsSignUp(true)}
                className={`relative z-10 flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isSignUp ? 'text-accent' : 'text-white/70 hover:text-white'
                }`}
              >
                Create account
              </button>
            </div>

            {isSignUp ? (
              <form onSubmit={signUpForm.handleSubmit(onSignUp)} className="space-y-5">
                <div className="space-y-4 rounded-xl border border-white/15 bg-black/2 p-5">
                  <h3 className="text-xs font-heading uppercase tracking-wide text-white/70">User details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" className="text-base font-medium text-white/90">
                        First name <span className="text-yellow-400">*</span>
                      </Label>
                      <input
                        id="first_name"
                        type="text"
                        placeholder="First name"
                        className={inputClass}
                        {...signUpForm.register('first_name')}
                      />
                      {signUpForm.formState.errors.first_name && (
                        <p className="text-sm text-yellow-400">{signUpForm.formState.errors.first_name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" className="text-base font-medium text-white/90">
                        Last name <span className="text-yellow-400">*</span>
                      </Label>
                      <input
                        id="last_name"
                        type="text"
                        placeholder="Last name"
                        className={inputClass}
                        {...signUpForm.register('last_name')}
                      />
                      {signUpForm.formState.errors.last_name && (
                        <p className="text-sm text-yellow-400">{signUpForm.formState.errors.last_name.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email" className="text-base font-medium text-white/90">
                      Email address <span className="text-yellow-400">*</span>
                    </Label>
                    <input
                      id="signup-email"
                      type="email"
                      placeholder="you@company.com"
                      className={inputClass}
                      {...signUpForm.register('email')}
                    />
                    {signUpForm.formState.errors.email && (
                      <p className="text-sm text-yellow-400">{signUpForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password" className="text-base font-medium text-white/90">
                      Password <span className="text-yellow-400">*</span>
                    </Label>
                    <div className="relative">
                      <input
                        id="signup-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="At least 6 characters"
                        className={`${inputClass} pr-12`}
                        {...signUpForm.register('password')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {signUpForm.formState.errors.password && (
                      <p className="text-sm text-yellow-400">{signUpForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-base font-medium text-white/90">
                      Confirm password <span className="text-yellow-400">*</span>
                    </Label>
                    <div className="relative">
                      <input
                        id="confirm-password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirm your password"
                        className={`${inputClass} pr-12`}
                        {...signUpForm.register('confirmPassword')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {signUpForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-yellow-400">{signUpForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <label className="mt-6 pt-5 flex items-center gap-3 cursor-pointer group border-t border-white/10">
                    <span className="relative flex shrink-0">
                      <input
                        id="marketing-consent"
                        type="checkbox"
                        className="peer sr-only"
                        {...signUpForm.register('marketing_consent')}
                      />
                      <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all peer-checked:border-white peer-checked:bg-white peer-focus:border-white/60 group-hover:border-white/60 ${(signUpForm.watch('marketing_consent') ?? false) ? 'border-white bg-white' : 'border-white/40 bg-white/5'}`}>
                        {(signUpForm.watch('marketing_consent') ?? false) && <Check className="h-3 w-3 text-accent stroke-[3] animate-check-pop" />}
                      </span>
                    </span>
                    <span className="text-white/90 text-sm leading-snug">
                      I'd like to receive marketing and product updates via email.
                    </span>
                  </label>

                  <div className="pt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-white text-accent font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          Create account
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <form onSubmit={signInForm.handleSubmit(onSignIn)} className="space-y-5">
                <div className="space-y-4 rounded-xl border border-white/15 bg-black/2 p-5">
                  <h3 className="text-xs font-heading uppercase tracking-wide text-white/70">Login details</h3>
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-base font-medium text-white/90">
                      Email address <span className="text-yellow-400">*</span>
                    </Label>
                    <input
                      id="signin-email"
                      type="email"
                      placeholder="you@company.com"
                      className={inputClass}
                      {...signInForm.register('email')}
                    />
                    {signInForm.formState.errors.email && (
                      <p className="text-sm text-yellow-400">{signInForm.formState.errors.email.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signin-password" className="text-base font-medium text-white/90">
                      Password <span className="text-yellow-400">*</span>
                    </Label>
                    <div className="relative">
                      <input
                        id="signin-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        placeholder="Your password"
                        className={`${inputClass} pr-12`}
                        {...signInForm.register('password')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {signInForm.formState.errors.password && (
                      <p className="text-sm text-yellow-400">{signInForm.formState.errors.password.message}</p>
                    )}
                  </div>

                  <label className="mt-6 pt-5 flex items-center gap-3 cursor-pointer group border-t border-white/10">
                    <span className="relative flex shrink-0">
                      <input
                        id="remember-me"
                        type="checkbox"
                        className="peer sr-only"
                        {...signInForm.register('remember_me')}
                      />
                      <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all peer-checked:border-white peer-checked:bg-white peer-focus:border-white/60 group-hover:border-white/60 ${(signInForm.watch('remember_me') ?? true) ? 'border-white bg-white' : 'border-white/40 bg-white/5'}`}>
                        {(signInForm.watch('remember_me') ?? true) && <Check className="h-3 w-3 text-accent stroke-[3] animate-check-pop" />}
                      </span>
                    </span>
                    <span className="text-white/90 text-sm leading-snug">
                      Remember me on this device
                    </span>
                  </label>

                  <div className="pt-5">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-white text-accent font-medium hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        <>
                          Sign in
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            )}

            <Link
              to="/new-project"
              className="block text-center text-white/70 hover:text-white text-sm transition-colors"
            >
              New here? Get started with a project enquiry →
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
